import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Edit2, Trash2, Eye, EyeOff, Save, X, Image as ImageIcon, Type, AlignLeft, Upload } from 'lucide-react';
import api, { assetUrl } from '../../../services/api';

interface HeroCard {
  id: string;
  heading: string;
  description: string;
  image: string;
  video: string;
  mediaType: 'image' | 'video';
  category: string;
  mediaFit: 'fit' | 'fill';
  isActive: boolean;
  displayOrder: number;
}

interface HeroIntroForm {
  heading: string;
  description: string;
  button_text: string;
  button_action: 'modal' | 'link';
  button_link: string;
  layout_style: 'style1' | 'style2';
  hero_background_type: 'none' | 'image' | 'video';
  hero_background_media: string;
  hero_background_fit: 'fit' | 'cover';
}

const ModernHeroEditor: React.FC<{ heroSections?: any[]; onUpdate?: () => void }> = ({ heroSections, onUpdate }) => {
  const [heroCards, setHeroCards] = useState<HeroCard[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCard, setEditingCard] = useState<HeroCard | null>(null);
  const [formData, setFormData] = useState<HeroCard>({
    id: '',
    heading: '',
    description: '',
    image: '',
    video: '',
    mediaType: 'image',
    category: 'General',
    mediaFit: 'fill',
    isActive: true,
    displayOrder: 0
  });
  const [isPreview, setIsPreview] = useState(false);
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [heroIntro, setHeroIntro] = useState<HeroIntroForm>({
    heading: 'Emprise\nBright',
    description: 'Architecting machine intelligence into high-fidelity visual experiences.',
    button_text: 'Launch Experience',
    button_action: 'modal',
    button_link: '',
    layout_style: 'style1',
    hero_background_type: 'none',
    hero_background_media: '',
    hero_background_fit: 'fit'
  });
  const [savingIntro, setSavingIntro] = useState(false);
  const [uploadingHeroBackground, setUploadingHeroBackground] = useState(false);

  // Load hero cards from database on component mount
  useEffect(() => {
    loadHeroCards();
    loadHeroIntro();
  }, []);

  const loadHeroIntro = async () => {
    try {
      const response = await api.sectionApi.get('hero');
      const data = response?.data || {};
      setHeroIntro({
        heading: String(data.heading || 'Emprise\nBright'),
        description: String(data.description || 'Architecting machine intelligence into high-fidelity visual experiences.'),
        button_text: String(data.button_text || 'Launch Experience'),
        button_action: (data.button_action === 'link' ? 'link' : 'modal'),
        button_link: String(data.button_link || ''),
        layout_style: data.layout_style === 'style2' ? 'style2' : 'style1',
        hero_background_type: data.hero_background_type === 'video' ? 'video' : data.hero_background_type === 'image' ? 'image' : 'none',
        hero_background_media: String(data.hero_background_media || ''),
        hero_background_fit: data.hero_background_fit === 'cover' ? 'cover' : 'fit'
      });
    } catch {
      setHeroIntro({
        heading: 'Emprise\nBright',
        description: 'Architecting machine intelligence into high-fidelity visual experiences.',
        button_text: 'Launch Experience',
        button_action: 'modal',
        button_link: '',
        layout_style: 'style1',
        hero_background_type: 'none',
        hero_background_media: '',
        hero_background_fit: 'fit'
      });
    }
  };

  const handleSaveHeroIntro = async () => {
    try {
      setSavingIntro(true);
      await api.sectionApi.update('hero', {
        heading: heroIntro.heading,
        description: heroIntro.description,
        button_text: heroIntro.button_text,
        button_action: heroIntro.button_action,
        button_link: heroIntro.button_action === 'link' ? (heroIntro.button_link || null) : null,
        layout_style: heroIntro.layout_style,
        hero_background_type: heroIntro.hero_background_type,
        hero_background_media: heroIntro.hero_background_media || null,
        hero_background_fit: heroIntro.hero_background_fit
      });
      window.dispatchEvent(new CustomEvent('hero:updated', { detail: { action: 'hero-intro-updated' } }));
      alert('Hero intro content saved successfully!');
    } catch (error: any) {
      alert(`Failed to save hero intro: ${error?.message || 'Please try again.'}`);
    } finally {
      setSavingIntro(false);
    }
  };

  const handleHeroBackgroundUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
      alert('Only image or video files are allowed');
      return;
    }

    try {
      setUploadingHeroBackground(true);
      const response = await api.sectionApi.uploadMedia('hero', file);
      if (!response?.success || !response?.url) {
        throw new Error(response?.message || 'Upload failed');
      }
      setHeroIntro((prev) => ({
        ...prev,
        hero_background_type: response.media_type === 'video' ? 'video' : 'image',
        hero_background_media: String(response.url)
      }));
    } catch (error: any) {
      alert(error?.message || 'Failed to upload hero background');
    } finally {
      setUploadingHeroBackground(false);
      e.target.value = '';
    }
  };

  const initializeDatabase = async () => {
    try {
      // Check if database is empty
      const response = await api.heroApi.getAll();
      const existingCards = response.data || [];
      
      if (existingCards.length === 0) {
        // Add front-page HERO_CARDS to database (exact same data)
        const frontPageHeroCards = [
          {
            id: 'exhibitions',
            heading: 'Exhibitions',
            description: 'Immersive environments that captivate and engage.',
            image: 'https://images.unsplash.com/photo-1526498460520-4c246339dccb?auto=format&fit=crop&w=900&q=80',
            video: '',
            mediaType: 'image',
            category: 'Exhibitions',
            mediaFit: 'fill',
            isActive: true,
            displayOrder: 0
          },
          {
            id: 'events',
            heading: 'Events',
            description: 'Pulse-pounding visuals synchronized with sound.',
            image: 'https://images.unsplash.com/photo-1512428559087-560fa5ceab42?auto=format&fit=crop&w=900&q=80',
            video: '',
            mediaType: 'image',
            category: 'Events',
            mediaFit: 'fill',
            isActive: true,
            displayOrder: 1
          },
          {
            id: 'fit-out',
            heading: 'Fit-out',
            description: 'Architecture meets algorithm in spatial experiences.',
            image: 'https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=900&q=80',
            video: '',
            mediaType: 'image',
            category: 'Fit-out',
            mediaFit: 'fill',
            isActive: true,
            displayOrder: 2
          },
          {
            id: 'custom',
            heading: 'Custom Builds',
            description: 'Bespoke interactive systems for brands and installations.',
            image: 'https://images.unsplash.com/photo-1526498460520-4c246339dccb?auto=format&fit=crop&w=900&q=80',
            video: '',
            mediaType: 'image',
            category: 'Custom',
            mediaFit: 'fill',
            isActive: true,
            displayOrder: 3
          }
        ];

        // Add each card to database
        for (const card of frontPageHeroCards) {
          await api.heroApi.create(card);
        }

        // Reload cards after initialization
        await loadHeroCards();
        console.log('Database initialized with front-page HERO_CARDS');
        alert('Front-page hero cards added to dashboard successfully!');
      }
    } catch (error) {
      console.error('Error initializing database:', error);
      // If initialization fails, set fallback cards (same as front-page)
      setHeroCards([
        {
          id: 'exhibitions',
          heading: 'Exhibitions',
          description: 'Immersive environments that captivate and engage.',
          image: 'https://images.unsplash.com/photo-1526498460520-4c246339dccb?auto=format&fit=crop&w=900&q=80',
          video: '',
          mediaType: 'image',
          category: 'Exhibitions',
          mediaFit: 'fill',
          isActive: true,
          displayOrder: 0
        },
        {
          id: 'events',
          heading: 'Events',
          description: 'Pulse-pounding visuals synchronized with sound.',
          image: 'https://images.unsplash.com/photo-1512428559087-560fa5ceab42?auto=format&fit=crop&w=900&q=80',
          video: '',
          mediaType: 'image',
          category: 'Events',
          mediaFit: 'fill',
          isActive: true,
          displayOrder: 1
        },
        {
          id: 'fit-out',
          heading: 'Fit-out',
          description: 'Architecture meets algorithm in spatial experiences.',
          image: 'https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=900&q=80',
          video: '',
          mediaType: 'image',
          category: 'Fit-out',
          mediaFit: 'fill',
          isActive: true,
          displayOrder: 2
        },
        {
          id: 'custom',
          heading: 'Custom Builds',
          description: 'Bespoke interactive systems for brands and installations.',
          image: 'https://images.unsplash.com/photo-1526498460520-4c246339dccb?auto=format&fit=crop&w=900&q=80',
          video: '',
          mediaType: 'image',
          category: 'Custom',
          mediaFit: 'fill',
          isActive: true,
          displayOrder: 3
        }
      ]);
    }
  };

  const loadHeroCards = async () => {
    try {
      setLoading(true);
      const response = await api.heroApi.getAll();
      const cards = response.data || [];
      const mappedCards = cards.map((card: any) => ({
        id: String(card.id),
        heading: card.heading,
        description: card.description,
        image: assetUrl(card.background_image || card.image),
        video: assetUrl(card.video_url),
        mediaType: card.use_video && card.video_url ? 'video' : 'image',
        category: String(card.type || card.category || 'General'),
        mediaFit: card.media_fit === 'fit' ? 'fit' : 'fill',
        isActive: !!card.is_active,
        displayOrder: card.display_order || card.order || 0
      }));
      setHeroCards(mappedCards);
    } catch (error) {
      setHeroCards((heroSections || []).map((card: any) => ({
        id: String(card.id),
        heading: card.heading,
        description: card.description,
        image: assetUrl(card.background_image || card.image),
        video: assetUrl(card.video_url),
        mediaType: card.use_video && card.video_url ? 'video' : 'image',
        category: String(card.type || card.category || 'General'),
        mediaFit: card.media_fit === 'fit' ? 'fit' : 'fill',
        isActive: !!card.is_active,
        displayOrder: card.display_order || card.order || 0
      })));
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingCard(null);
    setFormData({
      id: Date.now().toString(),
      heading: '',
      description: '',
      image: '',
      video: '',
      mediaType: 'image',
      category: 'General',
      mediaFit: 'fill',
      isActive: true,
      displayOrder: heroCards.length
    });
    setImageFile(null);
    setMediaFile(null);
    setIsModalOpen(true);
  };

  const handleEdit = (card: HeroCard) => {
    setEditingCard(card);
    setFormData(card);
    setImageFile(null);
    setMediaFile(null);
    setIsModalOpen(true);
  };

  const handleMediaUpload = async (file: File) => {
    if (!file) return;
    
    console.log('=== IMAGE UPLOAD START ===');
    console.log('File:', file);
    console.log('File name:', file.name);
    console.log('File size:', file.size);
    console.log('File type:', file.type);
    
    if (file.size > 200 * 1024 * 1024) {
      alert('Media size should be less than 200MB');
      return;
    }
    
    const isVideo = file.type.startsWith('video/');
    const isImage = file.type.startsWith('image/');

    if (!isImage && !isVideo) {
      alert('Please upload an image or video file');
      return;
    }
    
    try {
      setUploading(true);
      
      if (!editingCard) {
        const tempUrl = URL.createObjectURL(file);
        setMediaFile(file);
        setFormData((prev) => ({
          ...prev,
          image: isImage ? tempUrl : '',
          video: isVideo ? tempUrl : '',
          mediaType: isVideo ? 'video' : 'image'
        }));
        return;
      }

      let imageUrl = null;
      let videoUrl = null;
      let uploadSuccess = false;
      
      if (editingCard && editingCard.id) {
        try {
          console.log(`🔄 Method 1: Using heroApi.${isVideo ? 'uploadVideo' : 'uploadImage'} for existing card`);
          console.log('Card ID:', editingCard.id);
          
          const response = isVideo
            ? await api.heroApi.uploadVideo(editingCard.id, file)
            : await api.heroApi.uploadImage(editingCard.id, file);
          console.log('Method 1 - Response:', response);
          
          if (response.success && response.data) {
            imageUrl = response.data.background_image || response.data.image || response.data.url || null;
            videoUrl = response.data.video_url || response.data.url || null;
            if ((isVideo && videoUrl) || (!isVideo && imageUrl)) {
              uploadSuccess = true;
              console.log('✅ Method 1 Success:', isVideo ? videoUrl : imageUrl);
            }
          }
        } catch (method1Error) {
          console.error('❌ Method 1 failed:', method1Error);
        }
      }
      
      // Final result
      if (uploadSuccess && (imageUrl || videoUrl)) {
        setFormData({
          ...formData,
          image: isVideo ? '' : String(imageUrl || ''),
          video: isVideo ? String(videoUrl || '') : '',
          mediaType: isVideo ? 'video' : 'image'
        });
        console.log('🎉 Media uploaded successfully:', isVideo ? videoUrl : imageUrl);
        
        // Refresh hero cards list to show updated image
        try {
          const response = await api.heroApi.getAll();
          const cards = response.data || [];
          
          // Map backend fields to frontend fields
          const mappedCards = cards.map((card: any) => ({
            id: card.id.toString(),
            heading: card.heading,
            description: card.description,
            image: assetUrl(card.background_image),
            video: assetUrl(card.video_url),
            mediaType: card.use_video && card.video_url ? 'video' : 'image',
            category: String(card.type || card.category || 'General'),
            mediaFit: card.media_fit === 'fit' ? 'fit' : 'fill',
            isActive: card.is_active,
            displayOrder: card.display_order || card.order || 0
          }));
          
          setHeroCards(mappedCards);
          console.log('🔄 Hero cards refreshed after upload');
        } catch (refreshError) {
          console.error('Error refreshing cards after upload:', refreshError);
        }
        
        alert('Media uploaded successfully!');
      } else {
        console.error('❌ All upload methods failed');
        alert('Server upload failed. Please check server configuration.');
      }
      
    } catch (error) {
      console.error('❌ Critical error in upload:', error);
      
      let errorMessage = 'Error uploading media.';
      if (error instanceof Error) {
        if (error.message.includes('401')) {
          errorMessage = 'Authentication error. Please login again.';
        } else if (error.message.includes('403')) {
          errorMessage = 'Permission denied. Check your account access.';
        } else if (error.message.includes('404')) {
          errorMessage = 'Upload endpoint not found. Please check server.';
        } else if (error.message.includes('500')) {
          errorMessage = 'Server error. Please try again later.';
        } else if (error.message.includes('CORS')) {
          errorMessage = 'Network error. Please check server CORS settings.';
        } else {
          errorMessage = `Upload error: ${error.message}`;
        }
      }
      
      alert(errorMessage);
    } finally {
      setUploading(false);
      console.log('=== IMAGE UPLOAD END ===');
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
        alert('Please select an image or video file');
        return;
      }
      if (file.size > 200 * 1024 * 1024) {
        alert('Media size should be less than 200MB');
        return;
      }
      handleMediaUpload(file);
    }
  };

  const handleDelete = async (id: string) => {
    // Confirm before delete
    const confirmDelete = window.confirm('Are you sure you want to delete this hero card?');
    if (!confirmDelete) return;
    
    try {
      // Make API call to delete card
      let resolvedId = id;
      try {
        const resolve = await api.heroApi.getById(id);
        if (resolve?.success && resolve.data?.id) {
          resolvedId = String(resolve.data.id);
        }
      } catch { /* ignore */ }
      await api.heroApi.delete(resolvedId);
      
      // Remove from state only after successful API call
      setHeroCards(heroCards.filter(card => card.id !== id));
      
      console.log('Deleted hero card with id:', id);
      
      if (onUpdate) onUpdate();
      
      // Show success message
      alert('Hero card deleted successfully!');
      
    } catch (error) {
      console.error('Error deleting hero card:', error);
      alert('Error deleting hero card. Please try again.');
    }
  };

  const handleToggleActive = async (id: string) => {
    try {
      // Find the card first
      const card = heroCards.find(card => card.id === id);
      if (!card) {
        alert('Hero card not found');
        return;
      }
      
      const newStatus = !card.isActive;
      
      // Update state optimistically
      const updatedCards = heroCards.map(card => 
        card.id === id ? { ...card, isActive: newStatus } : card
      );
      setHeroCards(updatedCards);
      
      // Make API call to update status
      let resolvedId = id;
      try {
        const resolve = await api.heroApi.getById(id);
        if (resolve?.success && resolve.data?.id) {
          resolvedId = String(resolve.data.id);
        }
      } catch { /* ignore */ }
      await api.heroApi.update(resolvedId, { is_active: newStatus });
      
      console.log('Toggled hero card status:', { id, newStatus });
      
      if (onUpdate) onUpdate();
      
      // Show success message
      const status = newStatus ? 'activated' : 'deactivated';
      alert(`Hero card ${status} successfully!`);
      
    } catch (error) {
      console.error('Error toggling hero card status:', error);
      // Revert state on error
      const revertedCards = heroCards.map(card => 
        card.id === id ? { ...card, isActive: !card.isActive } : card
      );
      setHeroCards(revertedCards);
      alert('Error updating hero card status. Please try again.');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingCard) {
        // Update existing card
        const updatedCard = { 
          ...formData,
          is_active: formData.isActive,
          type: formData.category || 'General',
          media_fit: formData.mediaFit,
          background_image: formData.mediaType === 'image' ? formData.image : null,
          video_url: formData.mediaType === 'video' ? formData.video : null,
          use_video: formData.mediaType === 'video'
        };
        console.log('Updating hero card:', { id: formData.id, data: updatedCard });
        // Resolve numeric id via GET to avoid 404s when id is non-numeric
        let resolvedId = formData.id;
        try {
          const resolve = await api.heroApi.getById(formData.id);
          if (resolve?.success && resolve.data?.id) {
            resolvedId = String(resolve.data.id);
          }
        } catch { /* ignore */ }
        const response = await api.heroApi.update(resolvedId, updatedCard);
        console.log('Update response:', response);
        
        // Update state only after successful API call
        setHeroCards(heroCards.map(card => 
          card.id === formData.id ? { ...card, ...formData } : card
        ));
        
        console.log('Updated hero card:', updatedCard);
      } else {
        // Create new card
        const newCard = {
          ...formData,
          is_active: formData.isActive,
          display_order: formData.displayOrder || heroCards.length,
          type: formData.category || 'General',
          media_fit: formData.mediaFit,
          background_image: null,
          video_url: null,
          use_video: false
        };
        console.log('Creating new hero card:', newCard);
        
        const response = await api.heroApi.create(newCard);
        console.log('Create response:', response);

        const createdId = response.data.id.toString();
        if (mediaFile) {
          if (mediaFile.type.startsWith('video/')) {
            await api.heroApi.uploadVideo(createdId, mediaFile);
          } else {
            await api.heroApi.uploadImage(createdId, mediaFile);
          }
        }
        
        await loadHeroCards();
        
        console.log('Created new hero card:', newCard);
      }
      
      setIsModalOpen(false);
      setEditingCard(null);
      
      // Reset form
      setFormData({
        id: '',
        heading: '',
        description: '',
        image: '',
        video: '',
        mediaType: 'image',
        category: 'General',
        mediaFit: 'fill',
        isActive: true,
        displayOrder: 0
      });
      setMediaFile(null);
      
      if (onUpdate) onUpdate();
      
      // Dispatch custom event to notify front-page of updates
      const savedCard = editingCard ? { ...editingCard, ...formData } : formData;
      window.dispatchEvent(new CustomEvent('hero:updated', { 
        detail: { action: editingCard ? 'updated' : 'created', card: savedCard } 
      }));
      
      // Show success message
      alert(editingCard ? 'Hero card updated successfully!' : 'Hero card created successfully!');
      
    } catch (error: any) {
      console.error('Error saving hero card:', error);
      alert(`Error saving hero card: ${error.response?.data?.message || error.message || 'Please try again.'}`);
    }
  };

  const sortedCards = [...heroCards].sort((a, b) => a.displayOrder - b.displayOrder);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Hero Section</h1>
          <p className="text-gray-400">Manage your homepage hero cards</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setIsPreview(!isPreview)}
            className="px-4 py-2 bg-purple-500/20 border border-purple-500/30 rounded-lg text-purple-400 hover:bg-purple-500/30 transition-colors flex items-center gap-2"
          >
            {isPreview ? <EyeOff size={18} /> : <Eye size={18} />}
            {isPreview ? 'Edit Mode' : 'Preview Mode'}
          </button>
          <button
            onClick={handleAdd}
            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-2"
          >
            <Plus size={18} />
            Add Hero Card
          </button>
        </div>
      </div>

      {/* Hero Intro Settings */}
      <div className="bg-[#0b0c15]/60 border border-white/10 rounded-2xl p-5 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-white">Hero Right Content</h2>
            <p className="text-sm text-gray-400">Edit heading, description and button text shown on front-page hero right side.</p>
          </div>
          <button
            type="button"
            onClick={handleSaveHeroIntro}
            disabled={savingIntro}
            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-2 disabled:opacity-60"
          >
            <Save size={16} />
            {savingIntro ? 'Saving...' : 'Save Intro'}
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm text-gray-300 mb-2">Heading</label>
            <textarea
              value={heroIntro.heading}
              onChange={(e) => setHeroIntro({ ...heroIntro, heading: e.target.value })}
              className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-orange-500 focus:outline-none resize-none"
              rows={3}
              placeholder={"Emprise\nBright"}
            />
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-2">Description</label>
            <textarea
              value={heroIntro.description}
              onChange={(e) => setHeroIntro({ ...heroIntro, description: e.target.value })}
              className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-orange-500 focus:outline-none resize-none"
              rows={3}
              placeholder="Architecting machine intelligence into high-fidelity visual experiences."
            />
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-2">Button Text</label>
            <input
              type="text"
              value={heroIntro.button_text}
              onChange={(e) => setHeroIntro({ ...heroIntro, button_text: e.target.value })}
              className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-orange-500 focus:outline-none"
              placeholder="Launch Experience"
            />
            <div className="mt-3 space-y-2">
              <label className="block text-xs text-gray-400">Button Action</label>
              <label className="inline-flex items-center gap-2 mr-4">
                <input
                  type="radio"
                  name="hero_intro_button_action"
                  checked={heroIntro.button_action === 'modal'}
                  onChange={() => setHeroIntro({ ...heroIntro, button_action: 'modal' })}
                />
                <span className="text-sm text-gray-300">Open Form</span>
              </label>
              <label className="inline-flex items-center gap-2">
                <input
                  type="radio"
                  name="hero_intro_button_action"
                  checked={heroIntro.button_action === 'link'}
                  onChange={() => setHeroIntro({ ...heroIntro, button_action: 'link' })}
                />
                <span className="text-sm text-gray-300">Custom Link</span>
              </label>
              {heroIntro.button_action === 'link' && (
                <input
                  type="url"
                  value={heroIntro.button_link}
                  onChange={(e) => setHeroIntro({ ...heroIntro, button_link: e.target.value })}
                  className="w-full mt-2 px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-orange-500 focus:outline-none"
                  placeholder="https://example.com"
                />
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-2">Style Layout</label>
            <select
              value={heroIntro.layout_style}
              onChange={(e) => setHeroIntro({
                ...heroIntro,
                layout_style: e.target.value === 'style2' ? 'style2' : 'style1'
              })}
              className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-orange-500 focus:outline-none"
            >
              <option value="style1">Style 1 - Current Stacked Cards</option>
              <option value="style2">Style 2 - Centered Reel Hero</option>
            </select>
            <p className="mt-2 text-xs text-gray-400">Style 2 centered heading, description, and one button dikhata hai jo hero reel page open karta hai.</p>
          </div>
          <div className="md:col-span-3 rounded-xl border border-white/10 bg-[#11131f] p-4">
            <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-4 items-start">
              <div>
                <label className="block text-sm text-gray-300 mb-2">Background Media</label>
                <select
                  value={heroIntro.hero_background_type}
                  onChange={(e) => setHeroIntro({
                    ...heroIntro,
                    hero_background_type: e.target.value === 'video' ? 'video' : e.target.value === 'image' ? 'image' : 'none',
                    hero_background_media: e.target.value === 'none' ? '' : heroIntro.hero_background_media
                  })}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-orange-500 focus:outline-none"
                >
                  <option value="none">No Background Media</option>
                  <option value="image">Image Background</option>
                  <option value="video">Video Background</option>
                </select>
                <p className="mt-2 text-xs text-gray-400">Style 2 ke background mein uploaded image ya video dikh sakta hai.</p>
                <label className="mt-4 block text-sm text-gray-300 mb-2">Media Fit</label>
                <select
                  value={heroIntro.hero_background_fit}
                  onChange={(e) => setHeroIntro({
                    ...heroIntro,
                    hero_background_fit: e.target.value === 'cover' ? 'cover' : 'fit'
                  })}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-orange-500 focus:outline-none"
                >
                  <option value="fit">Full Fit - poora image/video dikhe</option>
                  <option value="cover">Full Cover - section fill kare</option>
                </select>
                <p className="mt-2 text-xs text-gray-400">Fit mein media crop nahi hota. Cover mein poora section fill hota hai.</p>
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-2">Upload Background File</label>
                <input
                  type="file"
                  accept="image/*,video/mp4,video/webm,video/quicktime"
                  onChange={handleHeroBackgroundUpload}
                  disabled={uploadingHeroBackground}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-orange-500 file:text-white hover:file:bg-orange-600 cursor-pointer disabled:opacity-50"
                />
                {uploadingHeroBackground && (
                  <div className="mt-2 text-sm text-orange-400">Uploading background media...</div>
                )}
                {heroIntro.hero_background_media && (
                  <div className="mt-3 rounded-xl overflow-hidden border border-white/10 bg-black/20">
                    {heroIntro.hero_background_type === 'video' ? (
                      <video src={assetUrl(heroIntro.hero_background_media)} controls className={`w-full h-48 ${heroIntro.hero_background_fit === 'cover' ? 'object-cover' : 'object-contain'} bg-black`} />
                    ) : (
                      <img src={assetUrl(heroIntro.hero_background_media)} alt="Hero background" className={`w-full h-48 ${heroIntro.hero_background_fit === 'cover' ? 'object-cover' : 'object-contain'} bg-black`} />
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {sortedCards.map((card, index) => (
          <motion.div
            key={card.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`relative group overflow-hidden rounded-2xl border transition-all duration-300 ${
              card.isActive 
                ? 'border-orange-500/50 bg-gradient-to-br from-orange-500/10 to-purple-500/10' 
                : 'border-gray-700/50 bg-gray-800/50 opacity-60'
            }`}
          >
            {/* Image */}
            <div className="aspect-video relative overflow-hidden">
              <img 
                src={card.mediaType === 'video' ? '' : card.image} 
                alt={card.heading}
                className={`w-full h-full ${card.mediaFit === 'fit' ? 'object-contain bg-black' : 'object-cover'} transition-transform duration-500 group-hover:scale-110 ${card.mediaType === 'video' ? 'hidden' : ''}`}
              />
              {card.mediaType === 'video' && card.video && (
                <video
                  src={card.video}
                  className={`w-full h-full ${card.mediaFit === 'fit' ? 'object-contain bg-black' : 'object-cover'} transition-transform duration-500 group-hover:scale-110`}
                  muted
                  playsInline
                  autoPlay
                  loop
                />
              )}
              <div className="absolute left-4 top-4 rounded-full border border-white/15 bg-black/45 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.35em] text-white/80 backdrop-blur-sm">
                Emprise
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
              
              {/* Status Badge */}
              <div className="absolute top-4 right-4">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  card.isActive 
                    ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                    : 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
                }`}>
                  {card.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="mb-2 text-[11px] uppercase tracking-[0.35em] text-orange-300/80">{card.category}</p>
              <h3 className="text-xl font-bold text-white mb-2">{card.heading}</h3>
              <p className="text-gray-400 text-sm mb-4 line-clamp-2">{card.description}</p>
              
              {/* Actions */}
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(card)}
                  className="flex-1 px-3 py-2 bg-blue-500/20 border border-blue-500/30 rounded-lg text-blue-400 hover:bg-blue-500/30 transition-colors flex items-center justify-center gap-1"
                >
                  <Edit2 size={14} />
                  Edit
                </button>
                <button
                  onClick={() => handleToggleActive(card.id)}
                  className={`px-3 py-2 rounded-lg transition-colors flex items-center justify-center ${
                    card.isActive 
                      ? 'bg-gray-500/20 border border-gray-500/30 text-gray-400 hover:bg-gray-500/30' 
                      : 'bg-green-500/20 border border-green-500/30 text-green-400 hover:bg-green-500/30'
                  }`}
                >
                  {card.isActive ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
                <button
                  onClick={() => handleDelete(card.id)}
                  className="px-3 py-2 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 hover:bg-red-500/30 transition-colors"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50"
            onClick={() => setIsModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-gray-900 border border-gray-800 rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">
                  {editingCard ? 'Edit Hero Card' : 'Add Hero Card'}
                </h2>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <X size={20} className="text-gray-400" />
                </button>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Heading */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
                    <Type size={16} />
                    Heading
                  </label>
                  <input
                    type="text"
                    value={formData.heading}
                    onChange={(e) => setFormData({ ...formData, heading: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-orange-500 focus:outline-none"
                    placeholder="Enter heading..."
                    required
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
                    <AlignLeft size={16} />
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-orange-500 focus:outline-none resize-none"
                    rows={3}
                    placeholder="Enter description..."
                    required
                  />
                </div>

                {/* Image Upload */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
                    <Upload size={16} />
                    Hero Media
                  </label>
                  <div className="space-y-3">
                    {/* File Upload */}
                    <div className="relative">
                      <input
                        type="file"
                        accept="image/*,video/mp4,video/webm,video/quicktime"
                        onChange={handleImageChange}
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-orange-500 file:text-white hover:file:bg-orange-600 cursor-pointer"
                        disabled={uploading}
                        required={!editingCard && !formData.image && !formData.video}
                      />
                      {uploading && (
                        <div className="absolute inset-0 bg-gray-800/80 rounded-lg flex items-center justify-center">
                          <div className="text-orange-400 text-sm">Uploading...</div>
                        </div>
                      )}
                    </div>
                    
                    {/* Image Preview */}
                    {(formData.image || formData.video) && (
                      <div className="mt-3">
                        {formData.mediaType === 'video' && formData.video ? (
                          <video
                            src={formData.video}
                            controls
                            className={`w-full h-48 ${formData.mediaFit === 'fit' ? 'object-contain bg-black' : 'object-cover'} rounded-lg border border-gray-700`}
                          />
                        ) : (
                          <img 
                            src={formData.image} 
                            alt="Preview" 
                            className={`w-full h-48 ${formData.mediaFit === 'fit' ? 'object-contain bg-black' : 'object-cover'} rounded-lg border border-gray-700`}
                          />
                        )}
                        <p className="text-xs text-gray-400 mt-2">Media selected successfully</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Display Order */}
                <div>
                  <label className="text-sm font-medium text-gray-300 mb-2 block">
                    Category
                  </label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-orange-500 focus:outline-none"
                    placeholder="Exhibitions"
                    required
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-300 mb-2 block">
                    Display Order
                  </label>
                  <input
                    type="number"
                    value={formData.displayOrder}
                    onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-orange-500 focus:outline-none"
                    min="0"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-300 mb-2 block">
                    Media Fit
                  </label>
                  <select
                    value={formData.mediaFit}
                    onChange={(e) => setFormData({ ...formData, mediaFit: e.target.value === 'fit' ? 'fit' : 'fill' })}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-orange-500 focus:outline-none"
                  >
                    <option value="fill">Fill Box - pura box fill kare</option>
                    <option value="fit">Fit Box - poora media dikhe</option>
                  </select>
                </div>

                {/* Active Status */}
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="w-4 h-4 text-orange-500 bg-gray-800 border-gray-600 rounded focus:ring-orange-500"
                  />
                  <label htmlFor="isActive" className="text-sm text-gray-300">
                    Active (show on homepage)
                  </label>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="flex-1 px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-gray-300 hover:bg-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center justify-center gap-2"
                  >
                    <Save size={18} />
                    {editingCard ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ModernHeroEditor;
